import { create } from 'zustand';
import type { TicketType, KioskBundle } from '../lib/api';
import type { WalkUpBundle, WalkUpIssuedWristband, WalkUpItem } from '@autopia/api-contracts/kiosk';
// The attachment shape is ticketing's, not the kiosk's — the walk-up request
// merely carries it through to order creation.
import type {
  OrderAttractionAttachment,
  OrderAttractionAttachmentItem,
} from '@autopia/api-contracts/ticketing';

type LocalTicketType = TicketType & {
  isBundle?: boolean;
  bundleContents?: string[];
};

// ─── Cart types (kept for WalkUpPaymentScreen API-submission compatibility) ──

export interface CartLine {
  ticketTypeId: string;
  ticketTypeCode: string;
  name: string;
  unitPrice: number;
  quantity: number;
  isBundle?: boolean;
  maxQuantity?: number;
  slotId?: string | null;
  slotStartTime?: string | null;
  slotEndTime?: string | null;
  attractionId?: string | null;
}

// ─── Per-instance (parent → children) model ───────────────────────────────────

export interface InstanceAttraction {
  ticketTypeId: string;
  ticketTypeCode: string;
  name: string;
  unitPrice: number;
  attractionId: string;
  slotId: string | null;
  slotStartTime: string | null;
  slotEndTime: string | null;
}

/**
 * One attraction the bundle ITSELF includes, for one specific visitor.
 *
 * Not an add-on: it is already paid for by the bundle price, so it carries no
 * `unitPrice` and cannot be toggled off. What the visitor still owes it is a
 * time — and each visitor picks their own, which is why this is per
 * (attraction line × entry unit) rather than per attraction line.
 */
export interface BundleAttractionSlot {
  /** `BundleItem.itemKey` of the attraction line. */
  bundleItemKey: string;
  /** `BundleItem.itemKey` of the ENTRY line that owns it — the visitor. */
  parentEntryItemKey: string | null;
  /** Which unit of that entry line (0-based). */
  parentEntryUnitIndex: number;
  ticketTypeId: string;
  ticketTypeCode: string;
  name: string;
  /** PER ENTRY UNIT: redemptions this one visitor gets, not a ticket count. */
  quantity: number;
  slotId: string | null;
  slotStartTime: string | null;
  slotEndTime: string | null;
}

/** One line of a bundle's composition, as the storefront listing reports it. */
export interface BundleDefItem {
  itemKey: string;
  parentItemKey: string | null;
  ticketTypeId: string;
  ticketTypeCode: string;
  ticketTypeName: string;
  category: 'ENTRY' | 'ATTRACTION';
  quantity: number;
}

/**
 * A bundle's identity + composition, kept on every instance it produced so a
 * copy can be cloned (the "+" stepper) without re-fetching the catalog.
 */
export interface BundleDef {
  bundleId: string;
  bundleCode: string;
  bundleName: string;
  bundlePriceIdr: number;
  items: BundleDefItem[];
}

export interface ParentInstance {
  instanceId: string; // stable uuid per instance
  parentType: 'entry' | 'bundle';
  ticketTypeId: string;
  ticketTypeCode: string;
  name: string;
  unitPrice: number;
  isBundle: boolean;
  bundleContents?: string[];
  /**
   * Bundle instances only — the copy this visitor belongs to.
   *
   * A bundle admitting three visitors is THREE instances, because a visitor is
   * a wristband and an attraction ticket hangs off exactly one of them. They
   * all share this id, which is what lets pricing, the quantity stepper and
   * removal still treat the copy as the single indivisible thing it is.
   */
  bundleCopyId?: string;
  /** Bundle instances only — the composition, for cloning a copy. */
  bundle?: BundleDef;
  /** Bundle instances only — which ENTRY line of the bundle this visitor is. */
  parentEntryItemKey?: string | null;
  /** Bundle instances only — which unit of that entry line (0-based). */
  parentEntryUnitIndex?: number;
  /** Bundle instances only — the bundle's own attractions this visitor gets. */
  bundleAttractionSlots?: BundleAttractionSlot[];
  attractions: InstanceAttraction[];
}

export interface OrderDraft {
  orderId: string;
  orderNumber: string;
  total: number;
}

export interface PaymentIntent {
  paymentNumber?: string;
  qrPayload?: string;
  edcReferenceNumber?: string;
  expiresAt?: string;
  gatewayProvider?: 'MIDTRANS' | 'XENDIT' | 'DOKU';
}

interface WalkUpState {
  visitDate: string | null;
  /** Customer identity — collected at the start of the walk-up flow */
  customerName: string;
  customerEmail: string;
  /** Per-instance parent slots — ground truth for per-ticket attraction assignment */
  instances: ParentInstance[];
  /** Flat cart rebuilt from instances — used by API submission */
  cart: CartLine[];
  promoCode: string | null;
  orderDraft: OrderDraft | null;
  kioskTxId: string | null;
  paymentIntent: PaymentIntent | null;
  issuedWristbands: WalkUpIssuedWristband[];
  /** Booking number returned when the order reaches COMPLETED status */
  bookingNumber: string | null;

  // Mutations
  setVisitDate: (date: string | null) => void;
  setCustomerIdentity: (name: string, email: string) => void;
  setBookingNumber: (bookingNumber: string | null) => void;

  // Parent instance mutations (new per-instance model)
  addParentInstance: (type: LocalTicketType) => void;
  addBundleInstance: (bundle: KioskBundle) => void;
  removeParentInstance: (instanceId: string) => void;
  incrementParent: (ticketTypeId: string) => void;
  decrementParent: (ticketTypeId: string) => void;

  // Instance attraction mutations
  toggleInstanceAttraction: (instanceId: string, type: TicketType) => void;
  setInstanceAttractionSlot: (
    instanceId: string,
    ticketTypeId: string,
    slotId: string | null,
    slotStartTime: string | null,
    slotEndTime: string | null,
  ) => void;
  /**
   * Pick the time for one of the bundle's OWN attractions.
   *
   * Keyed by `bundleItemKey`, not ticketTypeId: a bundle may hold two lines of
   * the same ticket type, and the visitor is already pinned by `instanceId`.
   */
  setBundleAttractionSlot: (
    instanceId: string,
    bundleItemKey: string,
    slotId: string | null,
    slotStartTime: string | null,
    slotEndTime: string | null,
  ) => void;
  applyAttractionsToAll: (sourceInstanceId: string) => void;

  // Legacy cart mutations — retained for backwards compatibility with older screens
  addToCart: (line: Omit<CartLine, 'quantity'>) => void;
  incrementLine: (ticketTypeId: string) => void;
  decrementLine: (ticketTypeId: string) => void;
  removeLine: (ticketTypeId: string) => void;
  setSlotId: (ticketTypeId: string, slotId: string | null) => void;

  setPromoCode: (code: string | null) => void;
  setOrderDraft: (draft: OrderDraft | null) => void;
  setKioskTxId: (id: string | null) => void;
  setPaymentIntent: (intent: PaymentIntent | null) => void;
  setIssuedWristbands: (data: WalkUpIssuedWristband[]) => void;
  reset: () => void;
}

// Session-scoped persistence — survives page reload but not browser close.
// Only `kioskTxId` is persisted because the WalkUpSuccessScreen can re-fetch
// the full order envelope from kiosk-service using just the transaction id.
const TX_SESSION_KEY = 'autopia.walkUp.kioskTxId';

function readPersistedTxId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(TX_SESSION_KEY);
  } catch {
    return null;
  }
}

function writePersistedTxId(id: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (id) window.sessionStorage.setItem(TX_SESSION_KEY, id);
    else window.sessionStorage.removeItem(TX_SESSION_KEY);
  } catch {
    /* sessionStorage disabled — non-fatal */
  }
}

const initialState = {
  visitDate: null,
  customerName: '',
  customerEmail: '',
  instances: [] as ParentInstance[],
  cart: [] as CartLine[],
  promoCode: null,
  orderDraft: null,
  kioskTxId: null,
  paymentIntent: null,
  issuedWristbands: [] as WalkUpIssuedWristband[],
  bookingNumber: null,
};

/** The copy an instance belongs to. Non-bundle instances are their own copy. */
function copyIdOf(inst: ParentInstance): string {
  return inst.bundleCopyId ?? inst.instanceId;
}

// ─── Cart rebuild — project instances into flat CartLine[] for API ────────────

function rebuildCart(instances: ParentInstance[]): CartLine[] {
  const map = new Map<string, CartLine>();
  // A bundle copy is charged once however many visitors it admits. Counting
  // per instance would multiply its price by the size of the party.
  const seenCopies = new Set<string>();

  for (const inst of instances) {
    const copyId = copyIdOf(inst);
    const isRepeatVisitorOfCopy = inst.parentType === 'bundle' && seenCopies.has(copyId);
    seenCopies.add(copyId);

    if (!isRepeatVisitorOfCopy) {
      const existing = map.get(inst.ticketTypeId);
      if (existing) {
        existing.quantity += 1;
      } else {
        map.set(inst.ticketTypeId, {
          ticketTypeId: inst.ticketTypeId,
          ticketTypeCode: inst.ticketTypeCode,
          name: inst.isBundle ? (inst.bundle?.bundleName ?? inst.name) : inst.name,
          unitPrice: inst.unitPrice,
          quantity: 1,
          isBundle: inst.isBundle,
          attractionId: null,
        });
      }
    }

    // Add-ons are per visitor, so every instance contributes its own.
    // Bundle-included attractions are deliberately absent: the bundle price
    // already covers them, and a cart line would charge for them twice.
    for (const att of inst.attractions) {
      // Attractions are keyed by ticketTypeId + slotId so different slot picks stay separate
      const key = `${att.ticketTypeId}::${att.slotId ?? ''}`;
      const existingAtt = map.get(key);
      if (existingAtt) {
        existingAtt.quantity += 1;
      } else {
        map.set(key, {
          ticketTypeId: att.ticketTypeId,
          ticketTypeCode: att.ticketTypeCode,
          name: att.name,
          unitPrice: att.unitPrice,
          quantity: 1,
          slotId: att.slotId,
          slotStartTime: att.slotStartTime,
          slotEndTime: att.slotEndTime,
          attractionId: att.attractionId,
        });
      }
    }
  }
  return Array.from(map.values());
}

function uid(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function makeInstance(type: LocalTicketType): ParentInstance {
  const isBundle = type.isBundle === true;
  return {
    instanceId: uid(),
    parentType: isBundle ? 'bundle' : 'entry',
    ticketTypeId: type.id,
    ticketTypeCode: type.code,
    name: type.name,
    unitPrice: type.resolvedPriceIdr ?? type.basePriceIdr,
    isBundle,
    bundleContents: type.bundleContents,
    attractions: [],
  };
}

function toBundleDef(bundle: KioskBundle): BundleDef {
  return {
    bundleId: bundle.id,
    bundleCode: bundle.code,
    bundleName: bundle.nameId ?? bundle.name,
    bundlePriceIdr: bundle.bundlePriceIdr,
    items: bundle.items.map((i) => ({
      itemKey: i.itemKey,
      parentItemKey: i.parentItemKey,
      ticketTypeId: i.ticketTypeId,
      ticketTypeCode: i.ticketTypeCode,
      ticketTypeName: i.ticketTypeNameId ?? i.ticketTypeName,
      category: i.category,
      quantity: i.quantity,
    })),
  };
}

function bundleContentsOf(def: BundleDef): string[] {
  return def.items.map((i) => `${i.quantity}× ${i.ticketTypeName}`);
}

/**
 * Explode one bundle copy into ONE INSTANCE PER VISITOR.
 *
 * A copy admitting Adult ×2 + Child ×1 is three wristbands, and each of them
 * picks its own attraction times and its own add-ons. Presenting the copy as a
 * single panel — which is what the kiosk did — sends every choice to the first
 * visitor and leaves the rest of the party unable to be given anything.
 *
 * The bundle's own attractions are distributed by `parentItemKey`, so a visitor
 * only ever sees the rides their own entry line carries.
 */
function makeBundleCopy(def: BundleDef): ParentInstance[] {
  const copyId = uid();
  const contents = bundleContentsOf(def);
  const entryLines = def.items.filter((i) => i.category === 'ENTRY');
  const attractionLines = def.items.filter((i) => i.category === 'ATTRACTION');

  const base = {
    parentType: 'bundle' as const,
    ticketTypeId: def.bundleId,
    ticketTypeCode: def.bundleCode,
    unitPrice: def.bundlePriceIdr,
    isBundle: true,
    bundleCopyId: copyId,
    bundle: def,
    bundleContents: contents,
  };

  // A bundle with no ENTRY line cannot mint parented attraction tickets and is
  // rejected server-side. Keep it as one nameless panel rather than producing
  // zero — the failure then surfaces at order creation with a real message.
  if (entryLines.length === 0) {
    return [
      {
        ...base,
        instanceId: uid(),
        name: def.bundleName,
        parentEntryItemKey: null,
        parentEntryUnitIndex: 0,
        bundleAttractionSlots: attractionLines.map((line) => ({
          bundleItemKey: line.itemKey,
          parentEntryItemKey: null,
          parentEntryUnitIndex: 0,
          ticketTypeId: line.ticketTypeId,
          ticketTypeCode: line.ticketTypeCode,
          name: line.ticketTypeName,
          quantity: line.quantity,
          slotId: null,
          slotStartTime: null,
          slotEndTime: null,
        })),
        attractions: [],
      },
    ];
  }

  const instances: ParentInstance[] = [];
  for (const line of entryLines) {
    for (let unit = 0; unit < line.quantity; unit++) {
      const visitorLabel =
        line.quantity > 1 ? `${line.ticketTypeName} #${unit + 1}` : line.ticketTypeName;

      instances.push({
        ...base,
        instanceId: uid(),
        name: `${def.bundleName} — ${visitorLabel}`,
        parentEntryItemKey: line.itemKey,
        parentEntryUnitIndex: unit,
        bundleAttractionSlots: attractionLines
          .filter((a) => a.parentItemKey === line.itemKey)
          .map((a) => ({
            bundleItemKey: a.itemKey,
            parentEntryItemKey: line.itemKey,
            parentEntryUnitIndex: unit,
            ticketTypeId: a.ticketTypeId,
            ticketTypeCode: a.ticketTypeCode,
            name: a.ticketTypeName,
            quantity: a.quantity,
            slotId: null,
            slotStartTime: null,
            slotEndTime: null,
          })),
        attractions: [],
      });
    }
  }
  return instances;
}

export const useWalkUpStore = create<WalkUpState>((set) => ({
  ...initialState,
  // Hydrate kioskTxId from sessionStorage so a page reload during the success
  // screen recovers the active transaction and re-polls for order state.
  kioskTxId: readPersistedTxId(),

  setVisitDate: (date) => set({ visitDate: date }),

  setCustomerIdentity: (name, email) => set({ customerName: name, customerEmail: email }),

  // ─── Parent instance ──────────────────────────────────────────────────────

  addParentInstance: (type) =>
    set((state) => {
      const instances = [...state.instances, makeInstance(type)];
      return { instances, cart: rebuildCart(instances) };
    }),

  addBundleInstance: (bundle) =>
    set((state) => {
      const instances = [...state.instances, ...makeBundleCopy(toBundleDef(bundle))];
      return { instances, cart: rebuildCart(instances) };
    }),

  // Removing any visitor of a bundle removes the whole copy: a bundle is priced
  // and sold as one indivisible thing, and half a copy has no meaning either to
  // the customer or to order creation.
  removeParentInstance: (instanceId) =>
    set((state) => {
      const target = state.instances.find((i) => i.instanceId === instanceId);
      if (!target) return state;
      const copyId = copyIdOf(target);
      const instances = state.instances.filter((i) => copyIdOf(i) !== copyId);
      return { instances, cart: rebuildCart(instances) };
    }),

  incrementParent: (ticketTypeId) =>
    set((state) => {
      const source = state.instances.find((i) => i.ticketTypeId === ticketTypeId);
      if (!source) return state;

      // Bundles clone a whole copy — all its visitors — from the stored
      // composition, so the new copy is addressable exactly like the first.
      const added: ParentInstance[] = source.bundle
        ? makeBundleCopy(source.bundle)
        : [{ ...source, instanceId: uid(), attractions: [] }];

      const instances = [...state.instances, ...added];
      return { instances, cart: rebuildCart(instances) };
    }),

  decrementParent: (ticketTypeId) =>
    set((state) => {
      // Find the LAST copy of this parent and drop it entirely.
      let doomedCopyId: string | null = null;
      for (let i = state.instances.length - 1; i >= 0; i--) {
        const inst = state.instances[i]!;
        if (inst.ticketTypeId === ticketTypeId) {
          doomedCopyId = copyIdOf(inst);
          break;
        }
      }
      if (!doomedCopyId) return state;

      const instances = state.instances.filter((i) => copyIdOf(i) !== doomedCopyId);
      return { instances, cart: rebuildCart(instances) };
    }),

  // ─── Instance attractions ─────────────────────────────────────────────────

  toggleInstanceAttraction: (instanceId, type) =>
    set((state) => {
      const instances = state.instances.map((inst) => {
        if (inst.instanceId !== instanceId) return inst;
        const already = inst.attractions.some((a) => a.ticketTypeId === type.id);
        if (already) {
          return {
            ...inst,
            attractions: inst.attractions.filter((a) => a.ticketTypeId !== type.id),
          };
        }
        return {
          ...inst,
          attractions: [
            ...inst.attractions,
            {
              ticketTypeId: type.id,
              ticketTypeCode: type.code,
              name: type.name,
              unitPrice: type.resolvedPriceIdr ?? type.basePriceIdr,
              attractionId: type.attractionId ?? '',
              slotId: null,
              slotStartTime: null,
              slotEndTime: null,
            },
          ],
        };
      });
      return { instances, cart: rebuildCart(instances) };
    }),

  setInstanceAttractionSlot: (instanceId, ticketTypeId, slotId, slotStartTime, slotEndTime) =>
    set((state) => {
      const instances = state.instances.map((inst) => {
        if (inst.instanceId !== instanceId) return inst;
        return {
          ...inst,
          attractions: inst.attractions.map((a) =>
            a.ticketTypeId === ticketTypeId ? { ...a, slotId, slotStartTime, slotEndTime } : a,
          ),
        };
      });
      return { instances, cart: rebuildCart(instances) };
    }),

  setBundleAttractionSlot: (instanceId, bundleItemKey, slotId, slotStartTime, slotEndTime) =>
    set((state) => {
      const instances = state.instances.map((inst) => {
        if (inst.instanceId !== instanceId) return inst;
        return {
          ...inst,
          bundleAttractionSlots: (inst.bundleAttractionSlots ?? []).map((s) =>
            s.bundleItemKey === bundleItemKey ? { ...s, slotId, slotStartTime, slotEndTime } : s,
          ),
        };
      });
      // Bundle-included slots never reach `cart` — they are already paid for —
      // so the projection is unchanged and rebuilding it would be wasted work.
      return { instances };
    }),

  applyAttractionsToAll: (sourceInstanceId) =>
    set((state) => {
      const source = state.instances.find((i) => i.instanceId === sourceInstanceId);
      if (!source) return state;
      const instances = state.instances.map((inst) => {
        if (inst.instanceId === sourceInstanceId) return inst;
        if (inst.ticketTypeId !== source.ticketTypeId) return inst;
        // An attraction the target's own bundle line already includes must not
        // also be bought as an add-on: order creation rejects the duplicate.
        const includedIds = new Set(
          (inst.bundleAttractionSlots ?? []).map((s) => s.ticketTypeId),
        );
        return {
          ...inst,
          attractions: source.attractions
            .filter((a) => !includedIds.has(a.ticketTypeId))
            .map((a) => ({ ...a })),
        };
      });
      return { instances, cart: rebuildCart(instances) };
    }),

  // ─── Legacy cart mutations (retained) ─────────────────────────────────────

  addToCart: (line) =>
    set((state) => {
      const existing = state.cart.find((l) => l.ticketTypeId === line.ticketTypeId);
      if (existing) {
        if (existing.maxQuantity !== undefined && existing.quantity >= existing.maxQuantity) return state;
        return {
          cart: state.cart.map((l) =>
            l.ticketTypeId === line.ticketTypeId ? { ...l, quantity: l.quantity + 1 } : l,
          ),
        };
      }
      return { cart: [...state.cart, { ...line, quantity: 1 }] };
    }),

  incrementLine: (ticketTypeId) =>
    set((state) => ({
      cart: state.cart.map((l) => {
        if (l.ticketTypeId !== ticketTypeId) return l;
        if (l.maxQuantity !== undefined && l.quantity >= l.maxQuantity) return l;
        return { ...l, quantity: l.quantity + 1 };
      }),
    })),

  decrementLine: (ticketTypeId) =>
    set((state) => ({
      cart: state.cart
        .map((l) => (l.ticketTypeId === ticketTypeId ? { ...l, quantity: l.quantity - 1 } : l))
        .filter((l) => l.quantity > 0),
    })),

  removeLine: (ticketTypeId) =>
    set((state) => ({ cart: state.cart.filter((l) => l.ticketTypeId !== ticketTypeId) })),

  setSlotId: (ticketTypeId, slotId) =>
    set((state) => ({
      cart: state.cart.map((l) => (l.ticketTypeId === ticketTypeId ? { ...l, slotId } : l)),
    })),

  setPromoCode: (code) => set({ promoCode: code }),
  setOrderDraft: (draft) => set({ orderDraft: draft }),
  setKioskTxId: (id) => {
    writePersistedTxId(id);
    set({ kioskTxId: id });
  },
  setPaymentIntent: (intent) => set({ paymentIntent: intent }),
  setIssuedWristbands: (data) => set({ issuedWristbands: data }),
  setBookingNumber: (bookingNumber) => set({ bookingNumber }),

  reset: () => {
    writePersistedTxId(null);
    set(initialState);
  },
}));

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectSubtotal = (state: WalkUpState): number =>
  state.cart.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);

export const selectTotal = (state: WalkUpState): number => selectSubtotal(state);

export const selectLineCount = (state: WalkUpState): number =>
  state.cart.reduce((sum, l) => sum + l.quantity, 0);

export const selectIsCartEmpty = (state: WalkUpState): boolean =>
  state.instances.length === 0;

export const selectHasEntryTicket = (state: WalkUpState): boolean =>
  state.instances.length > 0;

/**
 * How many of this parent are in the cart, for the Step 1 steppers.
 *
 * Counts COPIES, not instances: a bundle admitting three visitors is still one
 * bundle on the card, and showing "3" would invite the visitor to press "−"
 * twice for a single purchase.
 */
export const selectParentQuantity = (ticketTypeId: string) => (state: WalkUpState): number => {
  const copies = new Set<string>();
  for (const inst of state.instances) {
    if (inst.ticketTypeId === ticketTypeId) copies.add(copyIdOf(inst));
  }
  return copies.size;
};

/**
 * Bundle copies currently in the cart, keyed by bundle id.
 *
 * A plain function over `instances`, not a store selector: it builds a fresh
 * object every call, and zustand compares selector results by reference — so
 * as a selector it would re-render on every unrelated store write.
 */
export const bundleCopyCounts = (instances: ParentInstance[]): Record<string, number> => {
  const copiesByBundle = new Map<string, Set<string>>();
  for (const inst of instances) {
    if (inst.parentType !== 'bundle') continue;
    const seen = copiesByBundle.get(inst.ticketTypeId) ?? new Set<string>();
    seen.add(copyIdOf(inst));
    copiesByBundle.set(inst.ticketTypeId, seen);
  }
  const out: Record<string, number> = {};
  for (const [bundleId, copies] of copiesByBundle) out[bundleId] = copies.size;
  return out;
};

/**
 * Is every attraction that needs a time given one?
 *
 * `needsSlot` is supplied by the caller because the store does not know the
 * catalog. Attractions with no active slot for the visit date are exempt —
 * without that, a bundle containing an unscheduled ride would leave the
 * continue button permanently disabled with nothing the visitor could press.
 */
export const allSlotsChosen = (
  instances: ParentInstance[],
  needsSlot: (ticketTypeId: string) => boolean,
): boolean =>
  instances.every(
    (inst) =>
      inst.attractions.every((a) => !needsSlot(a.ticketTypeId) || !!a.slotId) &&
      (inst.bundleAttractionSlots ?? []).every(
        (s) => !needsSlot(s.ticketTypeId) || !!s.slotId,
      ),
  );

// ─── Purchase payload ─────────────────────────────────────────────────────────
//
// `cart` is the display projection: it flattens every instance's attractions
// into merged lines, which is what the review screen wants and exactly what the
// order API must not receive. Sending an attraction as a plain item strips the
// parent it was attached to, and the fulfilment saga cannot then give the
// attraction ticket a parentTicketId — it aborts the whole order after the
// visitor has paid (#862). So the payload is derived from `instances`, which
// still holds the mapping, and the two halves are kept disjoint: parents in
// `items`, attractions in `attractionAttachments`.

/**
 * Parent ticket lines only, merged by ticket type in first-appearance order.
 *
 * That order is load-bearing: the saga mints one ticket per unit in the order
 * items arrive and buckets them by ticket type, so bucket position N is the
 * Nth instance of that type here — which is what `instanceIndex` below refers
 * to. Reordering one without the other silently re-parents attractions.
 */
export const selectWalkUpItems = (state: WalkUpState): WalkUpItem[] => {
  const byType = new Map<string, WalkUpItem>();
  for (const inst of state.instances) {
    // Bundles go in `bundles[]`, never here. A bundle's code is not a ticket
    // type code, so emitting it as an item makes order creation fail with
    // "Ticket type <BUNDLE_CODE> not found" — every kiosk bundle sale did.
    if (inst.parentType === 'bundle') continue;
    const existing = byType.get(inst.ticketTypeId);
    if (existing) {
      existing.quantity += 1;
    } else {
      byType.set(inst.ticketTypeId, {
        ticketTypeCode: inst.ticketTypeCode,
        quantity: 1,
      });
    }
  }
  return Array.from(byType.values());
};

/**
 * Bundle lines, merged by bundle id, in first-appearance order.
 *
 * `quantity` counts COPIES, not visitor instances — a copy admitting three
 * people is one bundle sold. Only identity and quantity matter: the server
 * reads the composition from the database and ignores whatever items the
 * request claims, so this cannot misrepresent what is in the bundle. `items`
 * is included because the contract still requires it.
 */
export const selectWalkUpBundles = (state: WalkUpState): WalkUpBundle[] => {
  const byBundle = new Map<string, WalkUpBundle>();
  const seenCopies = new Set<string>();

  for (const inst of state.instances) {
    if (inst.parentType !== 'bundle') continue;
    const copyId = copyIdOf(inst);
    if (seenCopies.has(copyId)) continue;
    seenCopies.add(copyId);

    const existing = byBundle.get(inst.ticketTypeId);
    if (existing) {
      existing.quantity += 1;
    } else {
      byBundle.set(inst.ticketTypeId, {
        bundleId: inst.ticketTypeId,
        bundleCode: inst.ticketTypeCode,
        quantity: 1,
        bundlePriceIdr: inst.unitPrice,
        items: (inst.bundle?.items ?? []).map((i) => ({
          ticketTypeCode: i.ticketTypeCode,
          category: i.category,
          quantity: i.quantity,
        })),
      });
    }
  }
  return Array.from(byBundle.values());
};

/**
 * The parent→child mapping for every attraction in the order.
 *
 * Standalone entry tickets get one attachment each. A BUNDLE COPY gets ONE
 * attachment for the whole copy however many visitors it admits, because
 * `(parentType, parentTemplateId, instanceIndex)` is all the server allows to
 * address a copy — the visitor is named per row instead, by
 * `parentEntryItemKey` + `parentEntryUnitIndex`.
 *
 * Two kinds of row travel in a bundle attachment and they must not be
 * confused. `fromBundleInclusion: true` rows annotate an attraction the bundle
 * already contains, supplying only its time; without the flag the server reads
 * them as new add-ons and both charges and mints them twice.
 */
export const selectAttractionAttachments = (
  state: WalkUpState,
): OrderAttractionAttachment[] => {
  const entryAttachments: OrderAttractionAttachment[] = [];
  const bundleAttachments = new Map<string, OrderAttractionAttachment>();

  const entrySeenPerType = new Map<string, number>();
  const copiesSeenPerBundle = new Map<string, number>();
  const copyIndexByCopyId = new Map<string, number>();

  for (const inst of state.instances) {
    if (inst.parentType === 'bundle') {
      const copyId = copyIdOf(inst);

      let copyIndex = copyIndexByCopyId.get(copyId);
      if (copyIndex === undefined) {
        copyIndex = copiesSeenPerBundle.get(inst.ticketTypeId) ?? 0;
        copiesSeenPerBundle.set(inst.ticketTypeId, copyIndex + 1);
        copyIndexByCopyId.set(copyId, copyIndex);
      }

      const label = inst.bundle?.bundleName ?? inst.name;
      const attachment =
        bundleAttachments.get(copyId) ??
        ({
          parentType: 'bundle',
          parentTemplateId: inst.ticketTypeId,
          instanceIndex: copyIndex,
          instanceLabel: `${label} ${copyIndex + 1}`,
          attractions: [],
        } satisfies OrderAttractionAttachment);

      const rows: OrderAttractionAttachmentItem[] = [];

      // The bundle's own attractions — a time, not a purchase. A row with no
      // slot chosen carries no information the server does not already have.
      for (const slot of inst.bundleAttractionSlots ?? []) {
        if (!slot.slotId) continue;
        rows.push({
          ticketTypeId: slot.ticketTypeId,
          ticketTypeCode: slot.ticketTypeCode,
          quantity: slot.quantity,
          slotId: slot.slotId,
          slotStartTime: slot.slotStartTime ?? undefined,
          slotEndTime: slot.slotEndTime ?? undefined,
          fromBundleInclusion: true,
          bundleItemKey: slot.bundleItemKey,
          ...(slot.parentEntryItemKey ? { parentEntryItemKey: slot.parentEntryItemKey } : {}),
          parentEntryUnitIndex: slot.parentEntryUnitIndex,
        });
      }

      // Genuine add-ons this visitor chose on top of the bundle.
      for (const a of inst.attractions) {
        rows.push({
          ticketTypeId: a.ticketTypeId,
          ticketTypeCode: a.ticketTypeCode,
          // One toggle per attraction per visitor — never more than one.
          quantity: 1,
          slotId: a.slotId ?? undefined,
          slotStartTime: a.slotStartTime ?? undefined,
          slotEndTime: a.slotEndTime ?? undefined,
          fromBundleInclusion: false,
          ...(inst.parentEntryItemKey ? { parentEntryItemKey: inst.parentEntryItemKey } : {}),
          parentEntryUnitIndex: inst.parentEntryUnitIndex ?? 0,
        });
      }

      if (rows.length > 0) {
        attachment.attractions.push(...rows);
        bundleAttachments.set(copyId, attachment);
      }
      continue;
    }

    const instanceIndex = entrySeenPerType.get(inst.ticketTypeId) ?? 0;
    entrySeenPerType.set(inst.ticketTypeId, instanceIndex + 1);

    // Instances with no attractions are omitted — an empty attachment would
    // mint no tickets and only add noise to the order metadata.
    if (inst.attractions.length === 0) continue;

    entryAttachments.push({
      parentType: 'entry',
      parentTemplateId: inst.ticketTypeId,
      instanceIndex,
      instanceLabel: `${inst.name} ${instanceIndex + 1}`,
      attractions: inst.attractions.map((a) => ({
        ticketTypeId: a.ticketTypeId,
        ticketTypeCode: a.ticketTypeCode,
        quantity: 1,
        slotId: a.slotId ?? undefined,
        slotStartTime: a.slotStartTime ?? undefined,
        slotEndTime: a.slotEndTime ?? undefined,
      })),
    });
  }

  return [...entryAttachments, ...bundleAttachments.values()];
};
